import React, { ReactElement, FunctionComponent, memo } from 'react'
import isEmpty from 'lodash/isEmpty'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import LabelFont from '../../Theme/Fonts/Label'
import { FontelloIconType } from '../../themes/FontelloIconType'
import SmallBodyFont from '../../Theme/Fonts/SmallBody'
import { TestID } from '../../utils/PropTypesDefinitions'
import { TintColor } from '../../BaseComponents/Image/ImageInterfaces'
import Divider from '../../BaseComponents/Divider'
import LinkProps from '../../Link/LinkProps'
import InfoLabel from '../../BaseComponents/InfoLabel'
import NavigationListImage, { FontelloIconTypeExtended, FontSize } from './NavigationListImage'
import Counter from './Counter'

export type NavigationListItemBasicProps = {
    /** title of the NavigationListItem */
    name: string
    /** boolean to determine if title should be bold or not */
    bold?: boolean
    /** type of the item which is shown on the right */
    imageType?: 'none' | 'image' | 'icon'
    /** info text for the NavigationListItem */
    text?: string
    /** image url if imageType is set to image */
    imageUrl?: string
    /** info about imageUrl for seo */
    alt?: string
    /** icon name if imageType is set to icon */
    imageIcon?: FontelloIconTypeExtended
    /** icon size if imageType is set to icon */
    iconSize?: FontSize
    /** specific category of the item, must match LabelProps.categoryType */
    categoryType?: 'PLAYER' | 'TEAM' | 'AUTHOR'
    /** colors the corresponding image in black or white */
    tintColor?: TintColor
    /** Background color of the element */
    backgroundColor?: string
    /** Hide Border */
    hideBorder?: boolean
    /** right space of the component
     *
     * @defaultValue 5
     * */
    marginRight?: number
    /** text to show a small info label on the right of the component */
    infoLabel?: string
} & TestID

export type NavigationListItemProps = NavigationListItemBasicProps &
    LinkProps & {
        /** icon name of the icon on the left */
        rightIcon?: FontelloIconType | 'none'
        /** icon color of the icon on the left */
        rightIconColor?: 'onyx' | 'keshi-3'
        /** right icon size */
        rightIconSize?: FontSize
        /** value of the small text which is shown left to the rightIcon */
        count?: number | string
        /** href string to navigate to the provided url (web only) */
        href?: string
        /**
         * callback function triggered when NavigationListItem is pressed
         *
         * @TJS-type function
         */
        onPress?: () => void
    }

const NavigationListItem: FunctionComponent<NavigationListItemProps> = ({
    rightIcon = 'scroll-right',
    rightIconColor = 'onyx-4',
    rightIconSize = 13,
    count,
    href,
    onPress,
    webRel,
    ...rest
}): ReactElement => {
    const counter = count ? <Counter count={count} /> : null

    const component = (
        <BasicListItem {...rest} counter={counter}>
            {rightIcon !== 'none' ? (
                <FontelloIcon
                    textAlign="right"
                    name={rightIcon}
                    fontSize={rightIconSize}
                    color={rightIconColor}
                />
            ) : null}
        </BasicListItem>
    )
    return onPress || href ? (
        <Touchable href={href} onPress={onPress} webRel={webRel}>
            {component}
        </Touchable>
    ) : (
        component
    )
}

type BasicProps = NavigationListItemBasicProps & {
    counter?: JSX.Element | null
}

export const BasicListItem: FunctionComponent<BasicProps> = ({
    name,
    imageType = 'none',
    bold,
    text,
    imageUrl,
    alt,
    imageIcon,
    iconSize,
    counter,
    categoryType,
    tintColor,
    backgroundColor = 'pearl',
    children,
    testID,
    hideBorder,
    infoLabel,
    marginRight = 5,
}): ReactElement => {
    const isMultiLine = !isEmpty(text)

    return (
        <Grid noExpand {...{ backgroundColor, testID }}>
            <Grid
                horizontal
                centerVertical
                noExpand
                minHeight={isMultiLine ? 0 : 44}
                paddingY={isMultiLine ? 3 : 0}
                paddingLeft={imageType === 'none' ? 5 : 0}
            >
                <NavigationListImage
                    {...{
                        iconSize,
                        imageType,
                        imageUrl,
                        alt,
                        imageIcon,
                        tintColor,
                        categoryType,
                    }}
                />
                <Grid minWidth={0}>
                    <SmallBodyFont bold={bold} numberOfLines={1} color="onyx">
                        {name}
                    </SmallBodyFont>
                    {text ? (
                        <LabelFont numberOfLines={3} color="keshi-3">
                            {text}
                        </LabelFont>
                    ) : null}
                </Grid>
                {counter}
                {infoLabel ? <InfoLabel infoLabel={infoLabel} /> : null}
                <Grid noExpand marginRight={marginRight} marginLeft={children ? 4 : 0}>
                    {children}
                </Grid>
            </Grid>
            {!hideBorder ? <Divider dark /> : null}
        </Grid>
    )
}

export default memo(NavigationListItem)
